import React from 'react';
import { Inputs, PackageType, IndustryType, RevenueCadence, INDUSTRY_DEFAULTS } from '../utils/roiCalculations';
interface AssumptionsSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  inputs: Inputs;
  onChange: (newInputs: Inputs) => void;
}
export function AssumptionsSidebar({
  isOpen,
  onClose,
  inputs,
  onChange
}: AssumptionsSidebarProps) {
  const handleChange = (field: keyof Inputs, value: string | number) => {
    onChange({
      ...inputs,
      [field]: value
    });
  };
  // Handle numeric input changes - allow empty string, convert to number only when valid
  const handleNumericChange = (field: keyof Inputs, value: string) => {
    // If empty, set to empty string to allow clearing
    if (value === '') {
      onChange({
        ...inputs,
        [field]: ''
      });
      return;
    }
    // Otherwise parse as float
    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      onChange({
        ...inputs,
        [field]: numValue
      });
    }
  };
  const handleIndustryChange = (newIndustry: IndustryType) => {
    const defaults = INDUSTRY_DEFAULTS[newIndustry];
    onChange({
      ...inputs,
      industry: newIndustry,
      ticket: defaults.ticket,
      gpm: defaults.gpm,
      salesFirst90Total: defaults.salesFirst90Total,
      salesVetMonthly: defaults.salesVetMonthly,
      revenueCadence: defaults.revenueCadence,
      residualTicket: defaults.residualTicket
    });
  };
  return <>
      {/* Backdrop */}
      {isOpen && <div className="fixed inset-0 bg-black/50 z-40 transition-opacity" onClick={onClose} data-id="element-1" data-mp="3hbh0epdse8ip" />}

      {/* Sidebar */}
      <div className={`fixed top-0 right-0 h-full w-full max-w-sm bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-in-out ${isOpen ? 'translate-x-0' : 'translate-x-full'}`} data-id="element-2" data-mp="37mpb6p0422bh">
        <div className="h-full flex flex-col" data-id="element-3" data-mp="3xdnjwlzvqeg">
          {/* Header */}
          <div className="flex items-center justify-between p-5 border-b border-gray-200 bg-navy" data-id="element-4" data-mp="3zgld2te2ntq">
            <h2 className="text-lg font-bold text-white" data-id="element-5" data-mp="dr9qvyyug3kd">
              Business Assumptions
            </h2>
            <button onClick={onClose} className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors" data-id="element-6" data-mp="4sxokzom7hc3">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-7" data-mp="3qzvza13josw3">
                <line x1="18" y1="6" x2="6" y2="18" data-id="element-8" data-mp="3d8zm9im3qka5" />
                <line x1="6" y1="6" x2="18" y2="18" data-id="element-9" data-mp="3daald54lr3ux" />
              </svg>
            </button>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto p-4" data-id="element-10" data-mp="33atmb6juvo7x">
            <div className="space-y-4" data-id="element-11" data-mp="33bbo0ipaeun8">
              {/* Company Info */}
              <div className="space-y-3" data-id="element-12" data-mp="2t5tm6pt976mx">
                <h3 className="text-sm font-bold text-navy uppercase tracking-wide" data-id="element-13" data-mp="2t8zai2u264hl">
                  Company Info
                </h3>

                <div data-id="element-14" data-mp="334j4uksz4v48">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-15" data-mp="3363zsbj7ag2m">
                    Company Name
                  </label>
                  <input type="text" value={inputs.companyName} onChange={e => handleChange('companyName', e.target.value)} className="w-full p-2 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-16" data-mp="2ag57m79pm2tq" />
                </div>

                <div data-id="element-17" data-mp="2p7496yc4nr3t">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-18" data-mp="1rpvtq04k8mm7">
                    Industry
                  </label>
                  <select value={inputs.industry} onChange={e => handleIndustryChange(e.target.value as IndustryType)} className="w-full p-2 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-19" data-mp="1wnfjnxamentz">
                    <option value="" data-id="element-20" data-mp="c04qhb5863sh">Select</option>
                    <option data-id="element-21" data-mp="76jxxymei20o">Windows</option>
                    <option data-id="element-22" data-mp="75s34q0mo8bg">Roofing</option>
                    <option data-id="element-23" data-mp="ky8ag30kasi6">Solar (Sales)</option>
                    <option data-id="element-24" data-mp="l12uhtfnybb2">Solar (Vertical)</option>
                    <option data-id="element-25" data-mp="gwcze6stx0r7">Solar (Community)</option>
                    <option data-id="element-26" data-mp="gzr07aluh1wj">Pest</option>
                    <option data-id="element-27" data-mp="uxfkohc02712">Fiber</option>
                    <option data-id="element-28" data-mp="uv386jp0gove">Deregulated Energy</option>
                    <option data-id="element-29" data-mp="pxtayhvdr8x0">Hardscaping</option>
                    <option data-id="element-30" data-mp="pvzzmp9to0io">Landscaping</option>
                    <option data-id="element-31" data-mp="2wnr4sa75ge27">Other</option>
                  </select>
                </div>

                <div data-id="element-32" data-mp="2rpz9r7b7kbts">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-33" data-mp="2rqr8us40krot">
                    Partnership
                  </label>
                  <select value={inputs.pkg} onChange={e => handleChange('pkg', e.target.value as PackageType)} className="w-full p-2 rounded border border-gray-300 text-sm focus:border-amber outline-none font-semibold" data-id="element-34" data-mp="179riy5ec8qzz">
                    <option value="" data-id="element-35" data-mp="ofw0pt5xxgol">Select</option>
                    <option value="advantage" data-id="element-36" data-mp="oja2az6s5luz">HIRE Advantage</option>
                  </select>
                </div>
              </div>

              {/* Ad Spend */}
              <div className="space-y-3 pt-4 border-t border-gray-200" data-id="element-37" data-mp="ekarpb2gfg5c">
                <h3 className="text-sm font-bold text-navy uppercase tracking-wide" data-id="element-38" data-mp="jh19q3xtklte">
                  Ad Spend
                </h3>

                <div data-id="element-39" data-mp="28xk5hf219nte">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-40" data-mp="28uy09m2m7kec">
                    Ad Spend per Month
                  </label>
                  <div className="relative" data-id="element-41" data-mp="2cfpxuigr9kfn">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs" data-id="element-42" data-mp="2ccbxtgvkdnbd">
                      $
                    </span>
                    <input type="number" min="0" step="100" value={inputs.adSpend} onChange={e => handleNumericChange('adSpend', e.target.value)} className="w-full p-2 pl-6 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-43" data-mp="22hjya7if8qdy" />
                  </div>
                </div>

                <div data-id="element-44" data-mp="2hcq7128wj3az">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-45" data-mp="2hd9okhun3poh">
                    Ad Cost per Hire
                  </label>
                  <div className="relative" data-id="element-46" data-mp="3957d9p5jikuj">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs" data-id="element-47" data-mp="393e6m9uxg0mj">
                      $
                    </span>
                    <input type="number" min="1" step="50" value={inputs.cph} onChange={e => handleNumericChange('cph', e.target.value)} className="w-full p-2 pl-6 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-48" data-mp="10l4gmb54dc9l" />
                  </div>
                </div>
              </div>

              {/* Revenue & Margins */}
              <div className="space-y-3 pt-4 border-t border-gray-200" data-id="element-49" data-mp="1oc9zkz6cwko4">
                <h3 className="text-sm font-bold text-navy uppercase tracking-wide" data-id="element-50" data-mp="1sctstrnt744z">
                  Revenue & Margins
                </h3>

                <div data-id="element-51" data-mp="1xdhtl51uazce">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-52" data-mp="22arfwbtxmuw0">
                    Revenue Cadence
                  </label>
                  <select value={inputs.revenueCadence} onChange={e => handleChange('revenueCadence', e.target.value as RevenueCadence)} className="w-full p-2 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-53" data-mp="1d6jbf4etymo0">
                    <option value="one-time" data-id="element-54" data-mp="uf0d3avr3wsb">One-Time Sale</option>
                    <option value="monthly" data-id="element-55" data-mp="pj2zhcbvh4j8">Monthly Residual</option>
                    <option value="quarterly" data-id="element-56" data-mp="1396fhlk5s208">Quarterly Residual</option>
                  </select>
                </div>

                <div data-id="element-57" data-mp="yhn9lp3da5vu">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-58" data-mp="yh55y7edkpdw">
                    {inputs.revenueCadence === 'one-time' ? 'Average Ticket' : 'Initial Sale Ticket'}
                  </label>
                  <div className="relative" data-id="element-59" data-mp="3ssg3rgdr9kaa">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs" data-id="element-60" data-mp="3sreoezppibmr">
                      $
                    </span>
                    <input type="number" min="0" step="500" value={inputs.ticket} onChange={e => handleNumericChange('ticket', e.target.value)} className="w-full p-2 pl-6 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-61" data-mp="3ivbq64xz6x53" />
                  </div>
                </div>

                {inputs.revenueCadence !== 'one-time' && <div data-id="element-62" data-mp="ad5karvk490y">
                    <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-63" data-mp="2q64fy8p5sj40">
                      {inputs.revenueCadence === 'monthly' ? 'Monthly Residual' : 'Quarterly Residual'}
                    </label>
                    <div className="relative" data-id="element-64" data-mp="3636x2xsbwk19">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs" data-id="element-65" data-mp="3b18cn8qjq17z">
                        $
                      </span>
                      <input type="number" min="0" step="10" value={inputs.residualTicket || 0} onChange={e => handleNumericChange('residualTicket', e.target.value)} className="w-full p-2 pl-6 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-66" data-mp="2wcc0azhgi21k" />
                    </div>
                  </div>}

                {inputs.revenueCadence === 'monthly' && <div data-id="element-67" data-mp="bkjx06v90v69">
                    <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-68" data-mp="gl7oetne3e10">
                      Monthly Attrition
                    </label>
                    <div className="relative" data-id="element-69" data-mp="213rtnyqt6r34">
                      <input type="number" min="0" max="100" step="1" value={inputs.monthlyAttrition || 2} onChange={e => handleNumericChange('monthlyAttrition', e.target.value)} className="w-full p-2 pr-6 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-70" data-mp="1w4xed6s1vknr" />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs" data-id="element-71" data-mp="2ost7y2mbowgb">
                        %
                      </span>
                    </div>
                  </div>}

                {inputs.revenueCadence === 'quarterly' && <div data-id="element-72" data-mp="2uhukdqtwe99x">
                    <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-73" data-mp="2uicotaqprtsx">
                      Quarterly Attrition
                    </label>
                    <div className="relative" data-id="element-74" data-mp="33hzhn85dnrad">
                      <input type="number" min="0" max="100" step="1" value={inputs.quarterlyAttrition || 5} onChange={e => handleNumericChange('quarterlyAttrition', e.target.value)} className="w-full p-2 pr-6 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-75" data-mp="33h7wysdbjdyv" />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs" data-id="element-76" data-mp="2u1i5l60907">
                        %
                      </span>
                    </div>
                  </div>}

                <div data-id="element-77" data-mp="f1xvc3w0v24c">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-78" data-mp="a5h9jgcaozuf">
                    Gross Profit Margin
                  </label>
                  <div className="relative" data-id="element-79" data-mp="56ecai2k3xx0">
                    <input type="number" min="0" max="100" step="1" value={inputs.gpm} onChange={e => handleNumericChange('gpm', e.target.value)} className="w-full p-2 pr-6 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-80" data-mp="14ss0bnp0hcs" />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs" data-id="element-81" data-mp="k1aot7hef7fw">
                      %
                    </span>
                  </div>
                </div>
              </div>

              {/* Sales Performance */}
              <div className="space-y-3 pt-4 border-t border-gray-200" data-id="element-82" data-mp="wbi8canl5rtf">
                <h3 className="text-sm font-bold text-navy uppercase tracking-wide" data-id="element-83" data-mp="11c5yyv7wk6n4">
                  Sales Performance
                </h3>

                <div data-id="element-84" data-mp="ndg47m8k2j1x">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-85" data-mp="ndy4qv293jfn">
                    Sales per 90 Day Rep
                  </label>
                  <input type="number" min="0" step="0.5" value={inputs.salesFirst90Total} onChange={e => handleNumericChange('salesFirst90Total', e.target.value)} className="w-full p-2 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-86" data-mp="1f42n6o6g9we8" />
                </div>

                <div data-id="element-87" data-mp="22slzdesmetbl">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-88" data-mp="22svn0vq70oe4">
                    Monthly Sales per Veteran
                  </label>
                  <input type="number" min="0" step="0.5" value={inputs.salesVetMonthly} onChange={e => handleNumericChange('salesVetMonthly', e.target.value)} className="w-full p-2 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-89" data-mp="3ngobab6ilgly" />
                </div>

                <div data-id="element-90" data-mp="2zmdi1e1b5avr">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-91" data-mp="2zmwonyliccuh">
                    Pullthrough / Realization
                  </label>
                  <div className="relative" data-id="element-92" data-mp="39j00xxa2prv5">
                    <input type="number" min="0" max="100" step="1" value={inputs.pullthrough} onChange={e => handleNumericChange('pullthrough', e.target.value)} className="w-full p-2 pr-6 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-93" data-mp="39lci4880by75" />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs" data-id="element-94" data-mp="15uy4fvhg4b90">
                      %
                    </span>
                  </div>
                </div>
              </div>

              {/* Retention Model */}
              <div className="space-y-3 pt-4 border-t border-gray-200" data-id="element-95" data-mp="i4asmz4et1h6">
                <h3 className="text-sm font-bold text-navy uppercase tracking-wide" data-id="element-96" data-mp="i2hmdkxpkj9p">
                  Retention Model
                </h3>

                <div data-id="element-97" data-mp="rvox7lb7wrh6">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-98" data-mp="rw6oe3j0wfi8">
                    Hired to Launched
                  </label>
                  <div className="relative" data-id="element-99" data-mp="3vjgvzypm3k0t">
                    <input type="number" min="0" max="100" step="1" value={inputs.pctLaunched} onChange={e => handleNumericChange('pctLaunched', e.target.value)} className="w-full p-2 pr-6 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-100" data-mp="3vi5pvs8p206j" />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs" data-id="element-101" data-mp="1tqruhc6y1dv6">
                      %
                    </span>
                  </div>
                </div>

                <div data-id="element-102" data-mp="1kyhj3rkbb2cc">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-103" data-mp="1kxod4st6ggv9">
                    Launched to Day 30
                  </label>
                  <div className="relative" data-id="element-104" data-mp="1azs17ysf1hw3">
                    <input type="number" min="0" max="100" step="1" value={inputs.pct30} onChange={e => handleNumericChange('pct30', e.target.value)} className="w-full p-2 pr-6 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-105" data-mp="1aygzs5mg0m85" />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs" data-id="element-106" data-mp="2pu7861oiemkd">
                      %
                    </span>
                  </div>
                </div>

                <div data-id="element-107" data-mp="2fy42wmyd27t7">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-108" data-mp="2fydkbm5cb6na">
                    Day 30 to Day 90
                  </label>
                  <div className="relative" data-id="element-109" data-mp="274inr2iaaoj9">
                    <input type="number" min="0" max="100" step="1" value={inputs.pct90} onChange={e => handleNumericChange('pct90', e.target.value)} className="w-full p-2 pr-6 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-110" data-mp="275a8fiacf1ur" />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs" data-id="element-111" data-mp="1nk0bfos84j3m">
                      %
                    </span>
                  </div>
                </div>

                <div data-id="element-112" data-mp="1wilq38ukrdz4">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-113" data-mp="1wi3ywqmrrpy2">
                    Day 90 to 1 Year
                  </label>
                  <div className="relative" data-id="element-114" data-mp="2696wvjkejot4">
                    <input type="number" min="0" max="100" step="1" value={inputs.pct1y} onChange={e => handleNumericChange('pct1y', e.target.value)} className="w-full p-2 pr-6 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-115" data-mp="268ykdss6ylig" />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs" data-id="element-116" data-mp="2iszp3zu7z3sp">
                      %
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>;
}